import type { FeatureTranslation } from "../types";

export const kartuEn: Record<string, FeatureTranslation> = {
  "kartu-siswa": {
    label: "Digital Student Card",
    navDescription: "RFID/QR with a spending balance",
    summary:
      "One card for identity, attendance, and cashless spending — with parents fully in control.",
    detail: {
      title: "One card for your child's *identity and spending money*",
      lead: "An RFID student card with a balance, a daily limit, and a transaction history open to parents",
      intro:
        "Children no longer need to carry cash. The card balance works in the canteen, the co-op, and the school shop, while parents set the daily limit and see every transaction from the app. A lost card is blocked from a phone — the balance stays safe.",
      highlights: [
        "A separate spending balance with a daily limit you set",
        "Top up at Alfamart, partner shops, by bank transfer, or with school staff",
        "Block instantly, change and reset the PIN yourself from the app",
      ],
      benefits: [
        {
          title: "Spending money that stays in check",
          body: "Parents set a daily limit and get a notification when it is reached, so pocket money is not gone in a single purchase.",
        },
        {
          title: "Top up from anywhere",
          body: "Add funds at Alfamart, partner shops near the school, by bank transfer, or directly with school staff — all landing in the same history.",
        },
        {
          title: "Every rupiah leaves a trace",
          body: "The transaction history shows the source, time, amount, and status of every top-up and purchase. No balance disappears without explanation.",
        },
        {
          title: "A lost card stops being a crisis",
          body: "Block the card from the app, issue a replacement, and the balance moves across. The PIN can be changed or reset at any time.",
        },
      ],
      screensTitle: "All of it from a parent's phone",
      screensLead:
        "Balance, history, and card security settings live in one app — no trip to the school office required.",
      screens: [
        {
          alt: "Student card detail showing balance and transaction history",
          caption: "Card Detail",
          note: "Spending balance, daily limit, and transaction history",
        },
        {
          alt: "Student card settings: limit, PIN, and card blocking",
          caption: "Card Settings",
          note: "Daily limit, PIN, access management, blocking",
        },
        {
          alt: "Topping up a student card in the CARDS app",
          caption: "Top Up Card",
          note: "Choose an amount and a payment method",
        },
      ],
      faq: [
        {
          q: "Can the card carry our school's design and logo?",
          a: "Yes. Student cards are printed with your school's identity and branding, complete with the student's photo and card number.",
        },
        {
          q: "What happens to the balance if a card is lost?",
          a: "Parents can block the card from the app immediately so it cannot be used by anyone. The balance stays recorded and moves to the replacement card.",
        },
        {
          q: "Is the same card used for attendance?",
          a: "Yes. The same card is tapped for daily attendance, activity attendance, and purchases in the canteen and school co-op.",
        },
      ],
      metaTitle: "Digital RFID Student Card with Balance & Limits",
      metaDescription:
        "An RFID student card for attendance and cashless spending. Parents set daily limits, follow the transaction history, and block the card from the app.",
      keywords: [
        "digital student card",
        "rfid student id card",
        "student card with balance",
        "cashless school card",
        "student spending card",
      ],
    },
  },

  "top-up-kartu": {
    label: "Card Top-Up",
    navDescription: "Alfamart, bank transfer, school staff",
    summary:
      "Add funds through retail chains, bank transfer, or the school counter — all in one history.",
    detail: {
      title: "Top up from *whichever place is closest* to the family",
      lead: "Alfamart, partner shops near the school, bank transfer, or school staff — every route is valid",
      intro:
        "Not every parent is comfortable with bank transfers, and not everyone can come to the school. So topping up is open through several routes at once, from the convenience store to the school counter, and all of them land in the same card history.",
      highlights: [
        "Top up at Alfamart and partner shops near the school",
        "Bank transfer and digital payment methods",
        "Staff-assisted top-up for parents who pay in cash",
      ],
      benefits: [
        {
          title: "Pick the route within reach",
          body: "Parents used to the convenience store can top up at Alfamart or a partner shop; those used to mobile banking can transfer. Nobody is forced into one method.",
        },
        {
          title: "Cash payers are still served",
          body: "School staff can top up on behalf of a parent who hands over cash, and the transaction is still recorded as an official top-up.",
        },
        {
          title: "Status is never in doubt",
          body: "Every top-up carries its own status — successful, pending, or failed — so there is no confusion when a balance has not appeared yet.",
        },
        {
          title: "Everything lands in one history",
          body: "Whichever route is used, the top-up joins the same card transaction history, complete with its source and timestamp.",
        },
      ],
      screensTitle: "Two steps, amount then method",
      screensLead:
        "The top-up flow is kept short so parents do not get lost partway through.",
      screens: [
        {
          alt: "Card top-up page with amount selection",
          caption: "Choose Amount",
          note: "Set how much to add to the balance",
        },
        {
          alt: "Payment method options for topping up a student card",
          caption: "Payment Method",
          note: "Retail, bank transfer, and digital payments",
        },
      ],
      faq: [
        {
          q: "How long before the balance appears?",
          a: "Top-ups through digital channels generally land immediately. Every transaction shows its own status, so parents know when something is still pending.",
        },
        {
          q: "Is there a fee for topping up at Alfamart?",
          a: "Service fees follow the terms of the payment channel chosen. The breakdown is shown before the payment is confirmed.",
        },
        {
          q: "What happens to the money if a top-up fails?",
          a: "A failed transaction is recorded with a failed status and does not add to the balance. The record stays in the history so it can be traced and followed up if needed.",
        },
      ],
      metaTitle: "Student Card Top-Up via Retail & Bank Transfer",
      metaDescription:
        "Add funds to a student card from Alfamart, partner shops, bank transfer, or school staff. Every top-up lands in one history with a clear status.",
      keywords: [
        "student card top up",
        "school card balance reload",
        "student card alfamart top up",
        "school payment top up",
        "student wallet reload",
      ],
    },
  },

  "limit-kartu": {
    label: "Limits & Parental Controls",
    navDescription: "Daily limits, PIN, card blocking",
    summary:
      "Daily limits, limit alerts, access management, PIN changes and resets, and card blocking from the app.",
    detail: {
      title: "Spending control stays *in the parent's hands*",
      lead: "Daily limits, alerts, PINs, and card blocking — all managed from the app",
      intro:
        "Giving a child a card with a balance does not mean giving up control. Parents set a daily spending cap, get notified when it is reached, and can block the card the moment it goes missing. Every setting sits on one page, with no need to call the school.",
      highlights: [
        "A daily limit you can change at any time",
        "A notification when the spending limit is reached",
        "Change PIN, reset PIN, block, and remove a card",
      ],
      benefits: [
        {
          title: "A spending cap you decide",
          body: "Set how much a child may spend in a day. The figure can be adjusted whenever you like, without replacing the card.",
        },
        {
          title: "Told at the time, not afterwards",
          body: "Limit alerts let parents notice when spending habits shift, rather than discovering it once the balance has already run out.",
        },
        {
          title: "PINs you can handle yourself",
          body: "Changing or resetting a PIN happens right in the app. There is no need to contact school staff simply because a child forgot it.",
        },
        {
          title: "Manage several cards",
          body: "One parent can add cards, manage access, and remove cards — practical for families with more than one child at the same school.",
        },
      ],
      screensTitle: "One page for every control",
      screensLead:
        "Security settings are gathered in a single place so they are quick to reach in a hurry.",
      screens: [
        {
          alt: "Card settings page with daily limit, PIN, and card blocking",
          caption: "Card Settings",
          note: "Daily limit, PIN, access management, and blocking",
        },
      ],
      faq: [
        {
          q: "Does the daily limit apply in the school canteen too?",
          a: "Yes. The daily limit applies to card spending, including transactions in the canteen, the co-op, and the school shop.",
        },
        {
          q: "What if a child loses their card during the school day?",
          a: "Parents can block the card instantly from the app, so it cannot be used by anyone from that moment on.",
        },
        {
          q: "Can one parent account manage several children's cards?",
          a: "Yes. Card addition and access management are available, so several cards can be run from a single account.",
        },
      ],
      metaTitle: "Daily Limits & Parental Controls for Student Cards",
      metaDescription:
        "Set daily spending limits, receive limit alerts, change and reset PINs, and block a student card directly from the parent app.",
      keywords: [
        "student card daily limit",
        "parental spending controls",
        "block student card",
        "student card pin",
        "school parent app",
      ],
    },
  },

  cazhpoin: {
    label: "CazhPOIN",
    navDescription: "Points balance & fund management",
    summary:
      "A points balance that can be purchased and managed, kept separate from card spending money.",
    detail: {
      title: "A points balance that *stands apart* from spending money",
      lead: "A separate layer of balance on the app home screen, with its own purchases and history",
      intro:
        "CazhPOIN is a points balance held at the parent account level, separate from the spending money attached to a child's card. Because it stands on its own, points can be managed and moved without disturbing the daily spending limit already set on the card.",
      highlights: [
        "A points balance separate from card spending money",
        "Point purchases and a history of how they are used",
        "Fund management from the app home screen",
      ],
      benefits: [
        {
          title: "Separate from the child's spending money",
          body: "The points balance sits at account level, not on the card. Changes to points do not affect the daily limit or a child's spending balance.",
        },
        {
          title: "On the first screen",
          body: "The CazhPOIN balance appears at the very top of the app home screen, so there is no need to hunt through a submenu.",
        },
        {
          title: "A history you can trace",
          body: "Every addition and use of points carries its own record, so a change in balance always has an explanation.",
        },
        {
          title: "Fund management in one place",
          body: "The fund management menu brings account-level balance settings together, alongside point purchases and history.",
        },
      ],
      screensTitle: "CazhPOIN at the top of the home screen",
      screensLead:
        "Placed above the student card, making clear that the two are different balances.",
      screens: [
        {
          alt: "CazhPOIN balance with Buy Points and History in the CARDS app",
          caption: "CazhPOIN Balance",
          note: "Buy points, history, and fund management",
        },
      ],
      faq: [
        {
          q: "How is CazhPOIN different from the card spending balance?",
          a: "CazhPOIN is a balance at the parent account level, while spending money is attached to the child's card and subject to the daily limit. The two stand apart.",
        },
        {
          q: "How are CazhPOIN added?",
          a: "Through the Buy Points menu on the app home screen. Every addition is recorded in the history, so changes in balance can be traced.",
        },
        {
          q: "Does every school enable CazhPOIN?",
          a: "Availability follows the service configuration at each school. Our team can explain the scope for your particular setup.",
        },
      ],
      metaTitle: "CazhPOIN — Points Balance in the CARDS App",
      metaDescription:
        "A points balance at parent account level, separate from student card spending money. Purchases, history, and fund management from the app home screen.",
      keywords: [
        "cazhpoin",
        "school app points balance",
        "cards points balance",
        "parent loyalty points school",
        "school app fund management",
      ],
    },
  },

  "kantin-toko": {
    label: "Canteen & Online Shop",
    navDescription: "Cashless spending & school storefront",
    summary:
      "The canteen and co-op take payment from the card, plus a school online shop in the parent app.",
    detail: {
      title: "A cashless canteen, *a school shop inside the app*",
      lead: "Canteen and co-op purchases run on the card balance, while school products get a storefront for parents",
      intro:
        "Loose change makes life hard for everyone — children lose it, canteen staff scramble for it, and parents have no idea where the money went. A card balance makes purchases cashless and recorded. The online shop takes it further: school products can be browsed from the app.",
      highlights: [
        "Pay in the canteen and co-op by tapping the student card",
        "Every purchase lands in the history parents can see",
        "A school online storefront complete with prices",
      ],
      benefits: [
        {
          title: "No more scrambling for change",
          body: "Payment comes from the card balance, so the amount is always exact. Children do not lose coins and canteen staff do not need a float.",
        },
        {
          title: "Parents can see the spending",
          body: "Canteen purchases join the card history, so spending habits are visible — with the daily limit still acting as the fence around them.",
        },
        {
          title: "School products get a storefront",
          body: "The online shop shows products with their prices and the school they come from, so parents can see what is available without asking first.",
        },
        {
          title: "Business unit income is recorded",
          body: "Because transactions are digital, canteen and co-op takings enter the school's records rather than ending up in a cash drawer.",
        },
      ],
      screensTitle: "The school storefront on the parent home screen",
      screensLead:
        "Canteen and co-op products appear with prices and their school of origin, alongside every other menu.",
      screens: [
        {
          alt: "School online shop storefront in the CARDS app",
          caption: "Online Shop",
          note: "School products with their unit prices",
        },
      ],
      faq: [
        {
          q: "Does the canteen need special hardware?",
          a: "Canteen transactions use the same student card, with a reader provided during implementation. Our team will fit the setup to your canteen's conditions.",
        },
        {
          q: "Can several canteen vendors be accounted for separately?",
          a: "Yes. Transactions are digital and therefore traceable, and the school's books can separate takings according to your revenue sharing arrangements.",
        },
        {
          q: "Can a child spend beyond their daily limit?",
          a: "No. The daily limit set by parents applies to card transactions, including purchases in the canteen and co-op.",
        },
      ],
      metaTitle: "Cashless School Canteen & Online Shop",
      metaDescription:
        "Canteen and co-op purchases by tapping a student card, with no cash. A school online storefront appears directly in the parent app.",
      keywords: [
        "cashless school canteen",
        "school canteen pos software",
        "digital school cooperative",
        "school online shop",
        "canteen payment student card",
      ],
    },
  },

  "pindah-saldo": {
    label: "Balance Transfer & Summary",
    navDescription: "Transfer between cards & financial summary",
    summary:
      "Move a balance between cards or into savings, with a financial summary per student.",
    detail: {
      title: "A balance you can *move*, not one stuck on a single card",
      lead: "Transfer a balance between cards and read the financial summary from the same page",
      intro:
        "Money left on one child's card need not be locked there. The transfer feature lets a balance be moved, while the financial summary condenses what came in and what was spent so parents do not have to add it up from a long list of transactions.",
      highlights: [
        "Move a balance from one card to another destination",
        "A summary of money received and money spent",
        "Incoming transfers recorded in the history",
      ],
      benefits: [
        {
          title: "The balance is not locked",
          body: "A remaining balance can be moved, for instance to a younger sibling's card or when a student changes cards — without a request that waits to be processed by hand.",
        },
        {
          title: "A summary that saves time",
          body: "Rather than reading through the history line by line, the financial summary already totals what came in and what was spent.",
        },
        {
          title: "Transfers leave a record",
          body: "An incoming transfer appears in the history with the originating card number, so the source of a balance can always be traced.",
        },
        {
          title: "On the same page as the balance",
          body: "Top up, financial summary, and transfer sit side by side on the card detail page — no switching menus.",
        },
      ],
      screensTitle: "Transfer and summary from the card detail",
      screensLead:
        "The three main actions sit directly beneath the balance, with the full history below.",
      screens: [
        {
          alt: "Card detail with Top Up, Financial Summary, and Transfer buttons",
          caption: "Card Detail",
          note: "Top Up, Financial Summary, and Transfer in one row",
        },
      ],
      faq: [
        {
          q: "Where can a balance be transferred to?",
          a: "Transfers are made between cards within the CARDS ecosystem. Every incoming amount is recorded in the destination card's history along with the originating card number.",
        },
        {
          q: "Is there a fee for transferring a balance?",
          a: "Fee terms follow your school's service configuration. The details are shown before the transfer is confirmed.",
        },
        {
          q: "What does the Financial Summary contain?",
          a: "The summary totals money received and spent on the card over a given period, as a condensed view of the more detailed transaction history.",
        },
      ],
      metaTitle: "Student Card Balance Transfer & Summary",
      metaDescription:
        "Move balances between student cards and read a summary of money received and spent. Every transfer is recorded with its originating card.",
      keywords: [
        "student card balance transfer",
        "school card fund transfer",
        "student financial summary",
        "student card statement",
        "school card transaction history",
      ],
    },
  },
  "kasir-kantin": {
    label: "Canteen POS App",
    navDescription: "CazhPOS: cashier, stock, pre-orders",
    summary:
      "CazhPOS — the point-of-sale app for canteen and co-op vendors: transactions, stock, pre-orders, and payouts.",
    detail: {
      title: "A canteen till that *speaks the student card's language*",
      lead: "CazhPOS serves the vendor's side: cashier transactions, products and stock, pre-orders, and paying out the takings",
      intro:
        "A cashless canteen needs two sides: students paying with their card, and vendors serving them. CazhPOS is that vendor side — the point-of-sale app where transactions are taken, stock is managed, and sales proceeds are withdrawn, all connected to the CARDS card ecosystem.",
      highlights: [
        "Take student card payments right at the till",
        "Manage products, stock, shops, and staff from one app",
        "Cashier summaries and reports, through to shop balance payouts",
      ],
      benefits: [
        {
          title: "No cash at the counter",
          body: "Student card payments land as recorded till transactions — no more small change and coin counting through a packed break time.",
        },
        {
          title: "Products and stock under control",
          body: "The product list and its stock are managed from the app, so vendors know what sells and when to restock.",
        },
        {
          title: "Pre-orders before the break",
          body: "Orders can come in ahead of time through pre-order, thinning out the canteen queue at peak hours.",
        },
        {
          title: "Takings that are clear and withdrawable",
          body: "Cashier summaries and reports show sales per period, customer debts are recorded, and the shop balance can be withdrawn — the same flow shown in the vendor payout video at a partner boarding school.",
        },
      ],
      screensTitle: "CazhPOS and CazhTap at the till",
      screensLead:
        "The two sides that make a cashless canteen run: the cashier app in the vendor's hands, and the tap device for student cards.",
      screens: [
        {
          alt: "CazhPOS dashboard with sales charts, products, shop balance, and its mobile app",
          caption: "The CazhPOS app",
          note: "Sales dashboard, products, staff, and shop balance — on the web and on the vendor's phone",
        },
        {
          alt: "CazhTap device for tapping student cards at the till",
          caption: "The CazhTap device",
          note: "Students simply tap their card on CazhTap; the transaction lands at the till instantly",
        },
        {
          alt: "QRIS payment and payment method options in the CazhPOS app",
          caption: "Flexible payments",
          note: "Beyond card balance, the till takes QRIS, cash, and other cashless methods",
        },
      ],
      faq: [
        {
          q: "Who uses CazhPOS — the school or the vendor?",
          a: "Canteen and co-op vendors. The school manages the card and student side, while CazhPOS is the seller's daily working tool on the premises.",
        },
        {
          q: "Can more than one member of staff use it?",
          a: "Yes. CazhPOS supports shop and staff management, so several cashiers can take turns serving at the same shop.",
        },
        {
          q: "How does a vendor receive their money?",
          a: "Sales accumulate as a shop balance that can be withdrawn through a recorded payout flow, complete with its sales summary.",
        },
      ],
      metaTitle: "School & Pesantren Canteen POS App",
      metaDescription:
        "CazhPOS: the point-of-sale app for school canteen and co-op vendors — take student card payments, manage stock and pre-orders, and withdraw the takings.",
      keywords: [
        "school canteen pos app",
        "pesantren canteen point of sale",
        "school co-op cashier app",
        "cashless school pos",
        "canteen vendor app",
      ],
    },
  },
};
